import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

// "Switch" device handoff — send the current page from this device to another
// of the SAME user's logged-in devices (phone <-> PC), with an Accept/Reject
// prompt on the receiving side. See backend/routes/handoff.js for the server
// half: a per-session SSE stream is the only realtime channel in this codebase
// (everything else here is polling) — this context is its sole consumer.
//
// One request in flight at a time in each direction is all the UI surfaces:
// - incomingQueue: requests aimed at THIS session, shown one at a time.
// - outgoing: the most recent request THIS session sent, tracked just long
//   enough to toast its outcome (accepted/rejected/expired/no-response).

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const REQUEST_TTL_MS = 60 * 1000;
// Local fallback in case the 'resolved' SSE push never arrives (stream drop,
// receiving tab backgrounded by the OS). A few seconds past the server's own
// TTL so a genuine accept/reject racing the timeout still wins.
const OUTGOING_FALLBACK_MS = REQUEST_TTL_MS + 4000;

const HandoffContext = createContext(null);

export const useHandoff = () => {
  const ctx = useContext(HandoffContext);
  if (!ctx) throw new Error("useHandoff must be used within a HandoffProvider");
  return ctx;
};

export const HandoffProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [targets, setTargets] = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [incomingQueue, setIncomingQueue] = useState([]); // [{id, from_device_label, path, expires_at}]
  const [outgoing, setOutgoing] = useState(null); // {id, to_device_label, path}
  const outgoingTimeoutRef = useRef(null);
  const esRef = useRef(null);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadTargets = useCallback(async () => {
    if (!token) return;
    setTargetsLoading(true);
    try {
      const res = await axios.get(`${API}/handoff/targets`, { headers: authHeaders() });
      setTargets(res.data?.targets || []);
    } catch (err) {
      console.error("Failed to load switch targets:", err);
      toast.error("Couldn't load your other devices");
    } finally {
      setTargetsLoading(false);
    }
  }, [token, authHeaders]);

  const openPicker = useCallback(() => {
    setPickerOpen(true);
    loadTargets();
  }, [loadTargets]);
  const closePicker = useCallback(() => setPickerOpen(false), []);

  const clearOutgoingTimeout = () => {
    if (outgoingTimeoutRef.current) {
      clearTimeout(outgoingTimeoutRef.current);
      outgoingTimeoutRef.current = null;
    }
  };

  const finishOutgoing = useCallback((status, id) => {
    setOutgoing((cur) => {
      if (!cur || cur.id !== id) return cur;
      clearOutgoingTimeout();
      const messages = {
        accepted: `Opened on ${cur.to_device_label || "the other device"}`,
        rejected: `Switch declined on ${cur.to_device_label || "the other device"}`,
        expired: "Switch request timed out",
        cancelled: null, // superseded by a newer request from this device — silent
      };
      const msg = messages[status];
      if (msg) (status === "accepted" ? toast.success : toast.info)(msg);
      return null;
    });
  }, []);

  const sendSwitch = useCallback(async (target) => {
    if (!token) return;
    try {
      const path = window.location.pathname + window.location.search;
      const res = await axios.post(
        `${API}/handoff/request`,
        { to_session_id: target.session_id, path },
        { headers: authHeaders() }
      );
      const row = res.data?.request;
      setPickerOpen(false);
      clearOutgoingTimeout();
      setOutgoing({ id: row.id, to_device_label: target.device_label, path });
      toast.success(`Switch request sent to ${target.device_label || "device"}`);

      outgoingTimeoutRef.current = setTimeout(async () => {
        try {
          const check = await axios.get(`${API}/handoff/${row.id}`, { headers: authHeaders() });
          const status = check.data?.request?.status;
          finishOutgoing(status === "pending" ? "expired" : status, row.id);
        } catch (_) {
          finishOutgoing("expired", row.id);
        }
      }, OUTGOING_FALLBACK_MS);
    } catch (err) {
      const msg = err?.response?.data?.message || "Couldn't send switch request";
      toast.error(msg);
    }
  }, [token, authHeaders, finishOutgoing]);

  const respondIncoming = useCallback(async (id, accept) => {
    try {
      const res = await axios.post(
        `${API}/handoff/${id}/${accept ? "accept" : "reject"}`,
        {},
        { headers: authHeaders() }
      );
      setIncomingQueue((q) => q.filter((r) => r.id !== id));
      return res.data?.request;
    } catch (err) {
      const msg = err?.response?.data?.message || "Couldn't respond to switch request";
      toast.error(msg);
      setIncomingQueue((q) => q.filter((r) => r.id !== id));
      return null;
    }
  }, [authHeaders]);

  // Drop a queued incoming request once its own expiry passes, even if no
  // server event ever confirms it — the popup shouldn't outlive its offer.
  useEffect(() => {
    if (incomingQueue.length === 0) return undefined;
    const id = setInterval(() => {
      const now = Date.now();
      setIncomingQueue((q) => q.filter((r) => new Date(r.expires_at).getTime() > now));
    }, 1000);
    return () => clearInterval(id);
  }, [incomingQueue.length]);

  // SSE connection: one per authenticated session, reopened whenever the token
  // changes (login/logout/refresh). EventSource retries drops on its own.
  useEffect(() => {
    if (!isAuthenticated || !token) {
      esRef.current?.close();
      esRef.current = null;
      return undefined;
    }

    const es = new EventSource(`${API}/handoff/events?token=${encodeURIComponent(token)}`);
    esRef.current = es;

    es.addEventListener("request", (e) => {
      try {
        const data = JSON.parse(e.data);
        setIncomingQueue((q) => (q.some((r) => r.id === data.id) ? q : [...q, data]));
      } catch (_) { /* ignore malformed event */ }
    });

    es.addEventListener("resolved", (e) => {
      try {
        const data = JSON.parse(e.data);
        finishOutgoing(data.status, data.id);
      } catch (_) { /* ignore malformed event */ }
    });

    return () => {
      es.close();
      if (esRef.current === es) esRef.current = null;
    };
  }, [isAuthenticated, token, finishOutgoing]);

  useEffect(() => () => clearOutgoingTimeout(), []);

  const value = {
    pickerOpen,
    targets,
    targetsLoading,
    openPicker,
    closePicker,
    sendSwitch,
    incomingRequest: incomingQueue[0] || null,
    respondIncoming,
    outgoing,
  };

  return <HandoffContext.Provider value={value}>{children}</HandoffContext.Provider>;
};

export default HandoffContext;
